import { useState, useRef } from "react";
import {
  MessageSquare,
  Plus,
  Send,
  X,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  RotateCcw,
  ImagePlus,
  Ban,
  Paperclip,
} from "lucide-react";
import InfoBentoCard from "@ui/InfoBentoCard";
import useFeedbackTickets from "@features/feedback/hooks/useFeedbackTickets";

const inputStyle = {
  background: "var(--bg-input)",
  border: "1px solid var(--border-input)",
};

function formatDate(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function StatusBadge({ status }) {
  if (status === "done") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-emerald-400 bg-emerald-500/15">
        <CheckCircle2 className="w-2.5 h-2.5" /> Done
      </span>
    );
  }
  if (status === "cancelled") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-500/15">
        <Ban className="w-2.5 h-2.5" /> Cancelled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-blue-400 bg-blue-500/15">
      Open
    </span>
  );
}

function ImageGallery({ images }) {
  if (!images || images.length === 0) return null;
  return (
    <div className="flex gap-2 flex-wrap mt-2">
      {images.map((img) => (
        <a key={img.id} href={img.imageUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={img.imageUrl}
            alt=""
            className="w-20 h-20 object-cover rounded"
            style={{ border: "1px solid var(--border-input)" }}
          />
        </a>
      ))}
    </div>
  );
}

// ── Comment thread ──
function CommentThread({ comments, ticketId, onAddComment }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    e.target.value = "";
  };

  const removeFile = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
  };

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await onAddComment(ticketId, { text, file });
      setText("");
      removeFile();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
      <p className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: "var(--text-muted)" }}>
        Discussion ({(comments || []).length})
      </p>

      {/* Existing comments */}
      {(comments || []).length > 0 && (
        <div className="space-y-2 mb-3">
          {comments.map((c) => (
            <div
              key={c.id}
              className="p-2.5 rounded-lg"
              style={{ background: "var(--bg-row-hover)" }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold" style={{ color: "var(--text-heading)" }}>
                  {c.author}
                </span>
                <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                  {formatDateTime(c.createdAt)}
                </span>
              </div>
              <p className="text-sm" style={{ color: "var(--text-body)" }}>
                {c.text}
              </p>
              {c.imageUrl && (
                <a href={c.imageUrl} target="_blank" rel="noopener noreferrer" className="inline-block mt-1.5">
                  <img src={c.imageUrl} alt="" className="w-24 h-24 object-cover rounded" style={{ border: "1px solid var(--border-input)" }} />
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reply form */}
      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex-1">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a reply…"
            rows={2}
            className="w-full rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
            style={{ ...inputStyle, color: "var(--text-body)" }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
          />
          {preview && (
            <div className="relative inline-block mt-1.5">
              <img src={preview} alt="" className="w-16 h-16 object-cover rounded" style={{ border: "1px solid var(--border-input)" }} />
              <button
                onClick={removeFile}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-600 text-white flex items-center justify-center"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <button
            onClick={() => fileRef.current?.click()}
            className="p-2 rounded-lg transition-colors"
            style={{ ...inputStyle, color: "var(--text-secondary)" }}
            title="Attach image"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <button
            onClick={handleSubmit}
            disabled={!text.trim() || submitting}
            className="p-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors disabled:opacity-40"
            title="Send reply"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
}

// ── Ticket card ──
function TicketCard({ ticket, contractors, onClose, onReopen, onAddComment }) {
  const [expanded, setExpanded] = useState(false);
  const [closeMsg, setCloseMsg] = useState("");
  const [closeReason, setCloseReason] = useState("done");
  const [showCloseForm, setShowCloseForm] = useState(false);

  const contractor = (contractors || []).find((c) => c.id === ticket.contractorId);
  const isClosed = ticket.status === "done" || ticket.status === "cancelled";

  const handleClose = () => {
    onClose(ticket.id, closeReason, closeMsg);
    setCloseMsg("");
    setShowCloseForm(false);
  };

  return (
    <InfoBentoCard
      accent={ticket.status === "done" ? "emerald" : ticket.status === "cancelled" ? "rose" : "blue"}
      className="mb-3"
    >
      <div className="flex items-start gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {ticket.images?.length > 0 && (
          <img
            src={ticket.images[0].imageUrl}
            alt=""
            className="w-14 h-14 object-cover rounded flex-shrink-0"
            style={{ border: "1px solid var(--border-input)" }}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <StatusBadge status={ticket.status} />
            {contractor && (
              <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
                {contractor.name}
              </span>
            )}
            <span className="text-xs ml-auto flex-shrink-0" style={{ color: "var(--text-muted)" }}>
              {formatDate(ticket.createdAt)}
            </span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: "var(--text-body)" }}>
            {expanded
              ? ticket.description
              : ticket.description.length > 120
              ? ticket.description.slice(0, 120) + "…"
              : ticket.description}
          </p>
          {/* Comment count preview when collapsed */}
          {!expanded && (ticket.comments?.length || 0) > 0 && (
            <span className="text-[10px] mt-1 inline-block" style={{ color: "var(--text-muted)" }}>
              {ticket.comments.length} comment{ticket.comments.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex-shrink-0 pt-1" style={{ color: "var(--text-muted)" }}>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {/* Expanded view */}
      {expanded && (
        <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          <ImageGallery images={ticket.images} />

          {/* Close reason display */}
          {isClosed && (
            <div className="mt-3 p-3 rounded-lg" style={{ background: "var(--bg-row-hover)" }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold" style={{ color: "var(--text-heading)" }}>
                  Closed as {ticket.closeReason || ticket.status}
                </span>
                <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                  {formatDate(ticket.closedAt)}
                </span>
              </div>
              {ticket.closeMessage && (
                <p className="text-sm" style={{ color: "var(--text-body)" }}>
                  {ticket.closeMessage}
                </p>
              )}
            </div>
          )}

          {/* Comment thread */}
          <CommentThread
            comments={ticket.comments}
            ticketId={ticket.id}
            onAddComment={onAddComment}
          />

          {/* Close / Reopen actions */}
          {!isClosed && !showCloseForm && (
            <div className="mt-3 pt-3 flex justify-end gap-2" style={{ borderTop: "1px solid var(--border-subtle)" }}>
              <button
                onClick={(e) => { e.stopPropagation(); setCloseReason("cancelled"); setShowCloseForm(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                style={{ ...inputStyle, color: "var(--text-secondary)" }}
              >
                <Ban className="w-3.5 h-3.5" /> Cancel Ticket
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setCloseReason("done"); setShowCloseForm(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Mark Done
              </button>
            </div>
          )}

          {!isClosed && showCloseForm && (
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }} onClick={(e) => e.stopPropagation()}>
              <p className="text-xs font-semibold mb-2" style={{ color: "var(--text-heading)" }}>
                {closeReason === "done" ? "Close as Done" : "Cancel Ticket"}
              </p>
              <textarea
                value={closeMsg}
                onChange={(e) => setCloseMsg(e.target.value)}
                placeholder={closeReason === "done" ? "Resolution notes (optional)…" : "Reason for cancellation (optional)…"}
                rows={2}
                className="w-full rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
                style={{ ...inputStyle, color: "var(--text-body)" }}
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => setShowCloseForm(false)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                  style={{ ...inputStyle, color: "var(--text-secondary)" }}
                >
                  Back
                </button>
                <button
                  onClick={handleClose}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-colors ${
                    closeReason === "done" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"
                  }`}
                >
                  {closeReason === "done" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                  {closeReason === "done" ? "Confirm Done" : "Confirm Cancel"}
                </button>
              </div>
            </div>
          )}

          {isClosed && (
            <div className="mt-3 pt-3 flex justify-end" style={{ borderTop: "1px solid var(--border-subtle)" }}>
              <button
                onClick={(e) => { e.stopPropagation(); onReopen(ticket.id); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                style={{ ...inputStyle, color: "var(--text-secondary)" }}
              >
                <RotateCcw className="w-3.5 h-3.5" /> Reopen
              </button>
            </div>
          )}
        </div>
      )}
    </InfoBentoCard>
  );
}

// ── Main Feedback page ──
export default function Feedback({ contractors }) {
  const { tickets, createTicket, closeTicket, reopenTicket, addComment } = useFeedbackTickets();
  const [showForm, setShowForm] = useState(false);
  const [description, setDescription] = useState("");
  const [contractorId, setContractorId] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const openCount = tickets.filter((t) => t.status === "open").length;

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selected]);
    const newPreviews = selected.map((f) => URL.createObjectURL(f));
    setPreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeFile = (index) => {
    URL.revokeObjectURL(previews[index]);
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setDescription("");
    setContractorId("");
    previews.forEach((p) => URL.revokeObjectURL(p));
    setFiles([]);
    setPreviews([]);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    try {
      await createTicket({
        description: description.trim(),
        contractorId: contractorId ? Number(contractorId) : null,
        files,
      });
      resetForm();
    } catch (err) {
      console.error("[Feedback] submit failed", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-6">
        <InfoBentoCard
          cornerGlow
          icon={<MessageSquare className="w-4 h-4" />}
          accent="blue"
          title="Feedback"
          subtitle={`${openCount} open ticket${openCount !== 1 ? "s" : ""}`}
          className="flex-1"
        />
        {/* Desktop button — hidden on mobile */}
        <button
          onClick={() => setShowForm((v) => !v)}
          className={`hidden md:flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex-shrink-0 ${
            showForm
              ? "text-gray-400 hover:text-gray-200"
              : "bg-indigo-600 hover:bg-indigo-500 text-white"
          }`}
          style={showForm ? { ...inputStyle, color: "var(--text-secondary)" } : undefined}
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? "Cancel" : "New Ticket"}
        </button>

        {/* Mobile FAB — fixed above bottom nav */}
        <button
          onClick={() => setShowForm((v) => !v)}
          className="md:hidden fixed right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95"
          style={{
            bottom: "calc(var(--bottom-nav-h, 64px) + 68px)",
            background: showForm ? "var(--bg-input)" : "rgb(79 70 229)",
            border: showForm ? "1px solid var(--border-input)" : "1px solid rgba(99,102,241,0.5)",
            color: showForm ? "var(--text-secondary)" : "white",
          }}
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          <span className="text-sm font-semibold">{showForm ? "Cancel" : "New Ticket"}</span>
        </button>
      </div>

      {/* New ticket form */}
      {showForm && (
        <InfoBentoCard accent="amber" className="mb-6">
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "var(--text-muted)" }}>
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue or feedback…"
                rows={3}
                className="w-full rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
                style={{ ...inputStyle, color: "var(--text-body)" }}
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "var(--text-muted)" }}>
                Contractor
              </label>
              <select
                value={contractorId}
                onChange={(e) => setContractorId(e.target.value)}
                className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                style={{ ...inputStyle, color: "var(--text-body)" }}
              >
                <option value="">-- Select contractor --</option>
                {(contractors || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.role ? `(${c.role})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "var(--text-muted)" }}>
                Images
              </label>
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                style={{ ...inputStyle, color: "var(--text-secondary)" }}
              >
                <ImagePlus className="w-4 h-4" /> Add Images
              </button>
              {previews.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {previews.map((src, i) => (
                    <div key={i} className="relative">
                      <img src={src} alt="" className="w-16 h-16 object-cover rounded" style={{ border: "1px solid var(--border-input)" }} />
                      <button
                        onClick={() => removeFile(i)}
                        className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-600 text-white flex items-center justify-center"
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-1">
              <button
                onClick={handleSubmit}
                disabled={!description.trim() || submitting}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </InfoBentoCard>
      )}

      {/* Ticket list */}
      {tickets.length === 0 && (
        <p className="text-sm text-center py-12" style={{ color: "var(--text-muted)" }}>
          No feedback tickets yet. Create one to get started.
        </p>
      )}
      {tickets.map((ticket) => (
        <TicketCard
          key={ticket.id}
          ticket={ticket}
          contractors={contractors}
          onClose={closeTicket}
          onReopen={reopenTicket}
          onAddComment={addComment}
        />
      ))}
    </div>
  );
}
